import {
	ComparisonOperator,
	PromotionType,
	CreditType,
	GameType,
	DayOfWeek,
	DepositHistoryPeriod,
} from '@/modules/promotion/type'
import { z } from 'zod'

const walletConditionDepositSchema = z.object({
	pgHard: z.boolean().optional().nullable(),
	jokerHard: z.boolean().optional().nullable(),
	jiliHard: z.boolean().optional().nullable(),
	ppHard: z.boolean().optional().nullable(),
	nlcHard: z.boolean().optional().nullable(),
})

const constraintSchema = z.discriminatedUnion(
	'constraintType',
	[
		z.object({
			constraintType: z.literal(PromotionType.NEW_USER),
			isNewUser: z.boolean(),
		}),
		z.object({
			constraintType: z.literal(PromotionType.FIRST_DEPOSIT),
			isFirstDeposit: z.boolean(),
		}),
		z.object({
			constraintType: z.literal(PromotionType.FIRST_TIME_OF_DAY),
			isFirstTimeOfDay: z.boolean(),
		}),
		z.object({
			constraintType: z.literal(PromotionType.DEPOSIT_AMOUNT),
			operator: z.nativeEnum(ComparisonOperator),
			amount: z.number(),
		}),
		z.object({
			constraintType: z.literal(PromotionType.DAY_OF_WEEK),
			days: z.array(z.nativeEnum(DayOfWeek)),
		}),
		z.object({
			constraintType: z.literal(PromotionType.DEPOSIT_HISTORY_AMOUNT),
			period: z.nativeEnum(DepositHistoryPeriod),
			operator: z.nativeEnum(ComparisonOperator),
			amount: z.number().min(0, 'จำนวนเงินต้องมากกว่าหรือเท่ากับ 0'),
		}),
	],
	{
		errorMap: () => ({ message: 'กรุณาเลือกประเภทเงื่อนไข' }),
	},
)

const presetConfigSchema = z.object({
	thresholdType: z.enum(['FIXED', 'MULTIPLIER']).default('FIXED'),
	turnoverThreshold: z
		.number()
		.min(0, 'จำนวนเทิร์นโอเวอร์ต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
	turnoverMultiplier: z
		.number()
		.min(0, 'จำนวนเท่าต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
	presetId: z
		.string({
			required_error: 'กรุณาเลือก RTP Preset',
			invalid_type_error: 'กรุณาเลือก RTP Preset',
		})
		.min(1, 'กรุณาเลือก RTP Preset'),
	presetNumber: z
		.number({ invalid_type_error: 'กรุณากรอกเปอร์เซ็น RTP' })
		.min(30, 'เปอร์เซ็น RTP ต้องอยู่ระหว่าง 30-99')
		.max(99, 'เปอร์เซ็น RTP ต้องอยู่ระหว่าง 30-99')
		.optional()
		.nullable(),
	order: z.number().optional(),
})

const poolPresetConfigSchema = z.object({
	rewardSize: z
		.number({ invalid_type_error: 'กรุณากรอกจำนวนรางวัล' })
		.min(0)
		.optional()
		.nullable(),
	rewardRatio: z
		.number({ invalid_type_error: 'กรุณากรอกเปอร์เซ็นต์เงินรางวัลลูกค้า' })
		.min(0)
		.optional()
		.nullable(),
	id: z.string().optional(),
	name: z.string().optional(),
	description: z.string().optional(),
	promotionId: z.string().optional(),
	savingSize: z.number().optional(),
})

export const depositPromotionFormSchema = z
	.object({
		name: z.string().min(1, 'กรุณากรอกชื่อโปรโมชั่น'),
		banner: z.string().optional(),
		shortDescription: z.string().optional(),
		description: z.string().optional(),
		order: z
			.number({
				required_error: 'กรุณากรอกลำดับ',
				invalid_type_error: 'กรุณากรอกลำดับ',
			})
			.min(0, 'ลำดับต้องมากกว่าหรือเท่ากับ 0'),
		minimumRankOrder: z.number().optional().nullable(),

		isActive: z.boolean(),
		creditType: z.nativeEnum(CreditType, {
			errorMap: () => ({ message: 'กรุณาเลือกประเภทเครดิต' }),
		}),
		creditAmount: z
			.number()
			.min(0, 'จำนวนเครดิตต้องมากกว่าหรือเท่ากับ 0')
			.nullable()
			.optional(),
		creditRatio: z
			.number()
			.min(0, 'เปอร์เซ็นต์เครดิตต้องมากกว่าหรือเท่ากับ 0')
			.nullable()
			.optional(),
		maxCreditAmount: z
			.number()
			.min(0, 'เครดิตสูงสุดต้องมากกว่าหรือเท่ากับ 0')
			.nullable()
			.optional(),
		fixedDepositAmount: z
			.number()
			.min(0, 'จำนวนเงินฝากคงที่ต้องมากกว่าหรือเท่ากับ 0')
			.nullable()
			.optional(),
		maximumCurrentCredit: z
			.number()
			.min(0, 'เครดิตคงเหลือสูงสุดต้องมากกว่าหรือเท่ากับ 0')
			.nullable()
			.optional(),
		gameTypes: z.array(z.nativeEnum(GameType)).optional(),
		whitelistGameProviderIds: z.array(z.string()).nullable(),
		turnoverRound: z
			.number({
				required_error: 'กรุณากรอกเทิร์นโอเวอร์',
				invalid_type_error: 'กรุณากรอกเทิร์นโอเวอร์',
			})
			.min(0, 'จำนวนรอบต้องมากกว่า 0'),
		rankExperiencePoint: z
			.number()
			.min(0, 'คะแนนประสบการณ์ต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		diamondAmount: z
			.number({
				invalid_type_error: 'กรุณากรอกจำนวนเพชร',
			})
			.int('จำนวนเพชรต้องเป็นจำนวนเต็ม')
			.min(0, 'จำนวนเพชรต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		minimumWithdrawCredit: z
			.number()
			.min(0, 'จำนวนถอนขั้นต่ำต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		maximumWithdrawCredit: z
			.number()
			.min(0, 'จำนวนถอนสูงสุดต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		maximumWithdrawMultiplierOfDeposit: z
			.number()
			.min(0, 'จำนวนเท่าต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		limitQuota: z
			.number()
			.min(0, 'จำนวนโควต้าต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		limitQuotaPerDay: z
			.number()
			.min(0, 'จำนวนโควต้าต่อวันต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		limitQuotaPerMonth: z
			.number()
			.min(0, 'จำนวนโควต้าต่อเดือนต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		perPlayerQuota: z
			.number()
			.min(0, 'จำนวนโควต้าต่อผู้เล่นต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		limitQuotaPerDayPerPlayer: z
			.number()
			.min(0, 'จำนวนโควต้าต่อวันต่อผู้เล่นต้องมากกว่าหรือเท่ากับ 0')
			.optional()
			.nullable(),
		hasExpiration: z.boolean(),
		isForPlayer: z.boolean(),
		isForGuestOnly: z.boolean(),
		isCoupon: z.boolean(),
		requireConnectedLine: z.boolean().optional(),
		allowInstantClaim: z.boolean().optional(),
		allowedTagIds: z.array(z.string()).optional(),
		startDate: z.string().nullable().optional(),
		endDate: z.string().nullable().optional(),
		dateRange: z
			.object({
				from: z.date().nullable().optional(),
				to: z.date().nullable().optional(),
			})
			.optional(),
		claimConstraint: z.array(constraintSchema),
		usePool: z
			.boolean()
			.optional()
			.nullable()
			.transform((v) => v ?? false),
		poolPresetConfigs: z.array(poolPresetConfigSchema).optional().nullable(),
		presetConfigs: z.array(presetConfigSchema).optional().nullable(),
		walletCondition: walletConditionDepositSchema.optional(),
	})
	.superRefine((data, ctx) => {
		if (data.creditType === CreditType.FIXED && !data.creditAmount) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'กรุณากรอกจำนวนเครดิต',
				path: ['creditAmount'],
			})
		}

		if (data.creditType === CreditType.RATIO && !data.creditRatio) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'กรุณากรอกเปอร์เซ็นต์จ่ายเครดิต',
				path: ['creditRatio'],
			})
		}

		// Validate pool preset configs when usePool is true
		if (data.usePool && data.poolPresetConfigs) {
			data.poolPresetConfigs.forEach((config, index) => {
				if (!config.rewardSize && config.rewardSize !== 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'กรุณากรอกเงินรางวัลเริ่มต้น',
						path: ['poolPresetConfigs', index, 'rewardSize'],
					})
				}

				if (!config.rewardRatio && config.rewardRatio !== 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'กรุณากรอกเปอร์เซ็นต์เงินรางวัลลูกค้า',
						path: ['poolPresetConfigs', index, 'rewardRatio'],
					})
				}
			})
		}

		// Validate preset configs: no duplicates, multiplier < turnoverRound
		if (data.presetConfigs && data.presetConfigs.length > 0) {
			const configs = data.presetConfigs

			configs.forEach((config, index) => {
				if (config.presetNumber == null) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'กรุณากรอกเปอร์เซ็น RTP',
						path: ['presetConfigs', index, 'presetNumber'],
					})
				}
			})

			// All configs must share the same thresholdType
			const types = configs.map((c) => c.thresholdType ?? 'FIXED')
			const uniqueTypes = new Set(types)
			if (uniqueTypes.size > 1) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'Preset ทั้งหมดต้องใช้ประเภทเงื่อนไขเดียวกัน (คงที่ หรือ เท่าของยอดฝาก)',
					path: ['presetConfigs'],
				})
				return
			}

			const fixedConfigs = configs.filter(
				(c) => !c.thresholdType || c.thresholdType === 'FIXED',
			)
			const multiplierConfigs = configs.filter(
				(c) => c.thresholdType === 'MULTIPLIER',
			)

			// No duplicate fixed thresholds
			const fixedValues = fixedConfigs.map((c) => c.turnoverThreshold ?? 0)
			fixedValues.forEach((val, i) => {
				if (fixedValues.indexOf(val) !== i) {
					const configIndex = configs.indexOf(fixedConfigs[i])
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'ค่า Threshold ซ้ำกัน กรุณาใช้ค่าที่ไม่ซ้ำกัน',
						path: ['presetConfigs', configIndex, 'turnoverThreshold'],
					})
				}
			})

			// No duplicate multipliers
			const multiplierValues = multiplierConfigs
				.map((c) => c.turnoverMultiplier)
				.filter((v): v is number => v != null)
			multiplierValues.forEach((val, i) => {
				if (multiplierValues.indexOf(val) !== i) {
					const configIndex = configs.indexOf(multiplierConfigs[i])
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'ค่า Multiplier ซ้ำกัน กรุณาใช้ค่าที่ไม่ซ้ำกัน',
						path: ['presetConfigs', configIndex, 'turnoverMultiplier'],
					})
				}
			})

			// Multiplier must be < turnoverRound
			if (data.turnoverRound != null) {
				configs.forEach((config, i) => {
					if (
						config.thresholdType === 'MULTIPLIER' &&
						config.turnoverMultiplier != null &&
						config.turnoverMultiplier >= data.turnoverRound
					) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `ค่า Multiplier ต้องน้อยกว่า Turnover Round (${data.turnoverRound}x)`,
							path: ['presetConfigs', i, 'turnoverMultiplier'],
						})
					}
				})
			}
		}
	})

export type DepositPromotionFormValues = z.infer<
	typeof depositPromotionFormSchema
>
